import Dexie, { type EntityTable } from "dexie";
import type {
  QueuedSubmission,
  FormDraft,
  CachedFormDefinition,
  QueuedAttachment,
  CachedJob,
  CachedJobForms,
  CachedJobFormPayload,
  StagedJobFile,
  CachedJobStatuses,
} from "./types";

/**
 * Persisted-locally audio session. Created the moment a tech taps Record so
 * the session exists in IndexedDB even if the device is offline. When
 * connectivity is available the sync loop POSTs `/api/audio-sessions` to
 * obtain a real `serverSessionId` and stores it here for chunk uploads.
 */
export type LocalAudioSession = {
  localSessionId: string;
  serverSessionId: string | null;
  /**
   * Capability token returned alongside `serverSessionId`, required on every
   * chunk upload and on `/finalize` (see lib/audio/session-access.ts). Stored
   * durably because a queued chunk may not drain for days, long after the page
   * that created the session is gone. Null on rows created before this field
   * existed — those fall back to the caller's session cookie.
   */
  serverToken: string | null;
  formUrl: string;
  jobId: string | null;
  consentConfirmed: boolean;
  startedAt: string;
  /** PENDING_CREATE → server row not yet created; ACTIVE → server has it; FINALIZED/DISCARDED → end states. */
  status:
    | "PENDING_CREATE"
    | "ACTIVE"
    | "PENDING_FINALIZE"
    | "FINALIZED"
    | "DISCARDED";
  /** Set once `/finalize` succeeds. */
  finalizedAt: string | null;
  /** Last error from the sync loop (for the UI). */
  lastError: string | null;
};

/**
 * Persisted-locally audio chunk. Holds the raw Blob so it survives reloads
 * and offline gaps. Status drives the sync loop:
 *  - PENDING: waiting to be uploaded
 *  - UPLOADING: in-flight
 *  - SYNCED: server has it; we can safely delete the blob (we keep the row
 *            for debugging but null out the Blob to free quota)
 *  - FAILED: upload errored more than MAX_CHUNK_RETRIES times
 */
export type LocalAudioChunk = {
  localChunkId: string;
  localSessionId: string;
  sequenceIndex: number;
  startSec: number;
  durationSec: number;
  mimeType: string;
  filename: string;
  /** Null after upload succeeds — we don't want to retain audio forever locally. */
  blob: Blob | null;
  status: "PENDING" | "UPLOADING" | "SYNCED" | "FAILED";
  retryCount: number;
  createdAt: string;
  lastError: string | null;
  /**
   * ISO timestamp set when the chunk was handed to the native background
   * uploader (status UPLOADING). Used to detect stale UPLOADING rows whose
   * background task was lost (e.g. app killed before the result was
   * reconciled) so they can be re-armed. Non-indexed — no Dexie migration.
   */
  uploadingAt?: string | null;
};

/**
 * Local PIN verifier for offline unlock. Written only after a server-confirmed
 * trusted-device PIN flow (PIN setup or online PIN sign-in), where the plaintext
 * PIN is briefly in the client. Its mere existence is the device-trust proxy:
 * the httpOnly `st_device` cookie can't be read from JS, but a verifier can only
 * have been minted on a trusted device, so we gate offline unlock on this row.
 *
 * `hash` is PBKDF2-SHA256(pin, salt, iterations); the plaintext PIN is never
 * stored. `sessionValidUntil` bounds offline access to the live session's
 * expiry. `failedAttempts` drives the lockout-wipe (see offline-auth.ts).
 */
export type LocalOfflineAuth = {
  userId: string;
  email: string;
  displayName: string | null;
  /** hex */
  salt: string;
  /** hex of PBKDF2-SHA256 derived bits */
  hash: string;
  iterations: number;
  /** ISO timestamp — offline unlock is refused past this. */
  sessionValidUntil: string;
  createdAt: string;
  failedAttempts: number;
  /**
   * The user's current merchant, cached so the field shell can boot sync with
   * no network — the sync DB is named `surveythanks_<merchantId>` and the usual
   * source (`/api/merchants/me`) is unreachable offline. Non-indexed, so adding
   * it needs no Dexie version bump. Optional: rows written before this field
   * existed have none until the online backfill fills it in.
   */
  merchantId?: string | null;
  /**
   * How this device unlocks offline. Absent = "pin" (every row written before
   * passkey unlock existed). A "passkey" row has empty salt/hash and carries
   * the public keys of the user's passkeys for THIS host instead — see
   * features/forms/offline/passkey-unlock.ts. Non-indexed: no version bump.
   */
  method?: "pin" | "passkey";
  passkeys?: OfflinePasskey[];
};

/** A passkey's public half, cached so the device can check an unlock offline. */
export type OfflinePasskey = {
  /** base64url, as the assertion's `id` */
  credentialId: string;
  /** base64 COSE_Key, exactly as Passkey.publicKey stores it */
  publicKey: string;
  rpId: string;
};

class OfflineFormsDB extends Dexie {
  formDefinitions!: EntityTable<CachedFormDefinition, "formUrl">;
  formDrafts!: EntityTable<FormDraft, "draftId">;
  queuedSubmissions!: EntityTable<QueuedSubmission, "localSubmissionId">;
  queuedAttachments!: EntityTable<QueuedAttachment, "localAttachmentId">;
  audioSessions!: EntityTable<LocalAudioSession, "localSessionId">;
  audioChunks!: EntityTable<LocalAudioChunk, "localChunkId">;
  offlineAuth!: EntityTable<LocalOfflineAuth, "userId">;
  cachedJobs!: EntityTable<CachedJob, "jobId">;
  cachedJobForms!: EntityTable<CachedJobForms, "jobId">;
  cachedJobStatuses!: EntityTable<CachedJobStatuses, "id">;
  /** Compound primary key `[formUrl+jobId]` — see CachedJobFormPayload. */
  jobFormPayloads!: EntityTable<CachedJobFormPayload, "formUrl">;
  stagedJobFiles!: EntityTable<StagedJobFile, "stagedFileId">;

  constructor() {
    super("SurveyThanksOffline");

    this.version(1).stores({
      formDefinitions: "formUrl",
      formDrafts: "draftId, formUrl",
      queuedSubmissions:
        "localSubmissionId, formUrl, status, idempotencyKey, createdAt",
      queuedAttachments: "localAttachmentId, localSubmissionId",
    });

    /**
     * v2 — adds audio assistant local persistence. Dexie auto-migrates by
     * leaving v1 tables intact and creating the new ones.
     */
    this.version(2).stores({
      formDefinitions: "formUrl",
      formDrafts: "draftId, formUrl",
      queuedSubmissions:
        "localSubmissionId, formUrl, status, idempotencyKey, createdAt",
      queuedAttachments: "localAttachmentId, localSubmissionId",
      audioSessions: "localSessionId, status, formUrl, jobId, startedAt",
      audioChunks: "localChunkId, localSessionId, status, sequenceIndex",
    });

    /**
     * v3 — adds the offline PIN verifier table for trusted-device offline
     * unlock. Dexie auto-migrates by leaving existing tables intact.
     */
    this.version(3).stores({
      formDefinitions: "formUrl",
      formDrafts: "draftId, formUrl",
      queuedSubmissions:
        "localSubmissionId, formUrl, status, idempotencyKey, createdAt",
      queuedAttachments: "localAttachmentId, localSubmissionId",
      audioSessions: "localSessionId, status, formUrl, jobId, startedAt",
      audioChunks: "localChunkId, localSessionId, status, sequenceIndex",
      offlineAuth: "userId, createdAt",
    });

    /**
     * v4 — adds the offline caches behind the job → linked-form flow so a
     * job-bound form can open with no network. `jobFormPayloads` uses a compound
     * `[formUrl+jobId]` primary key (one form linked to N jobs → N payloads).
     * Dexie auto-migrates by leaving existing tables intact.
     */
    this.version(4).stores({
      formDefinitions: "formUrl",
      formDrafts: "draftId, formUrl",
      queuedSubmissions:
        "localSubmissionId, formUrl, status, idempotencyKey, createdAt",
      queuedAttachments: "localAttachmentId, localSubmissionId",
      audioSessions: "localSessionId, status, formUrl, jobId, startedAt",
      audioChunks: "localChunkId, localSessionId, status, sequenceIndex",
      offlineAuth: "userId, createdAt",
      cachedJobs: "jobId, fetchedAt",
      cachedJobForms: "jobId, fetchedAt",
      cachedJobStatuses: "id, fetchedAt",
      jobFormPayloads: "[formUrl+jobId], formUrl, jobId, fetchedAt",
    });
    /**
     * v5 — adds `stagedJobFiles`, the local blob store for photos captured on a
     * job offline. Indexed by jobId (list a job's staged photos) and uploaded
     * (find blobs still needing a PUT). Additive; Dexie leaves other tables intact.
     */
    this.version(5).stores({
      formDefinitions: "formUrl",
      formDrafts: "draftId, formUrl",
      queuedSubmissions:
        "localSubmissionId, formUrl, status, idempotencyKey, createdAt",
      queuedAttachments: "localAttachmentId, localSubmissionId",
      audioSessions: "localSessionId, status, formUrl, jobId, startedAt",
      audioChunks: "localChunkId, localSessionId, status, sequenceIndex",
      offlineAuth: "userId, createdAt",
      cachedJobs: "jobId, fetchedAt",
      cachedJobForms: "jobId, fetchedAt",
      cachedJobStatuses: "id, fetchedAt",
      jobFormPayloads: "[formUrl+jobId], formUrl, jobId, fetchedAt",
      stagedJobFiles: "stagedFileId, jobId, uploaded",
    });
  }
}

export const offlineDb = new OfflineFormsDB();

/**
 * Safari (iPad/iPhone) drops the IndexedDB connection while the app is in the
 * background; the next read then fails with "UnknownError: Attempt to iterate a
 * cursor that doesn't exist" / "Connection to Indexed Database server lost", and
 * keeps failing until a reload (PostHog: every such error was Mobile Safari on
 * /submit). Dexie only works around this for Chrome. Closing with auto-open left
 * on makes the next query reopen a fresh connection.
 */
function isLostConnection(err: unknown): boolean {
  const e = err as { name?: string; message?: string } | null;
  return (
    e?.name === "UnknownError" ||
    /Indexed Database server lost|cursor that doesn't exist/i.test(e?.message ?? "")
  );
}

function resetOfflineDbConnection(): void {
  try {
    offlineDb.close({ disableAutoOpen: false });
  } catch {
    // already closed
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (ev) => {
    if (isLostConnection(ev.reason)) resetOfflineDbConnection();
  });
  // Back from the background: probe with a cheap read and reconnect if it died,
  // before the form's own reads hit the dead connection.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    offlineDb.queuedAttachments.count().catch((err) => {
      if (isLostConnection(err)) resetOfflineDbConnection();
    });
  });
}
